(ns storrito.connect3.email-connect-panel
  (:require [clojure.string :as str]
            [storrito.ui.button :as button]
            [storrito.connect3.common :as common]
            [storrito.connect3.login-panel :as login-panel]))

(defn- parse-url
  [s]
  (try
    (js/URL. (str/trim s))
    (catch :default _ nil)))

(defn- instagram-host?
  [host]
  (or (= host "instagram.com")
      (str/ends-with? host ".instagram.com")))

(defn- login-link-params?
  "True when the URL carries the query parameters that Instagram's
   one-click login needs (cf. `loginWithEmailLink` in the PHP client)."
  [url]
  (let [params (.-searchParams url)]
    (or (.has params "uid")
        (.has params "token"))))

(defn valid-login-link?
  "True when the pasted link looks like the link behind the 'Log in as ...'
   button in Instagram's password-reset email. Users regularly paste other
   links here (e.g. their own Storrito connect-link) which starts a connect
   session that can only fail."
  [link]
  (boolean
    (when-let [url (parse-url (or link ""))]
      (and (instagram-host? (.-hostname url))
           (login-link-params? url)))))

(defn get-login-link-validation-error [link]
  (when (and (string? link)
             (seq (str/trim link)))
    (let [url (parse-url link)]
      (cond
        (nil? url)
        "This is not a link. Please paste the copied link address."

        (not (instagram-host? (.-hostname url)))
        "This is not an Instagram link. Please copy the link address of the \"Log in as ...\" button in the email from Instagram."

        (not (login-link-params? url))
        "This Instagram link is not the login link. Please copy the link address of the \"Log in as ...\" button in the email from Instagram."))))

(defn- step [number content]
  [:div.flex.gap-3.items-start
   [:div.flex-shrink-0.w-7.h-7.rounded-full.bg-blue-100.text-blue-700.flex.items-center.justify-center.text-sm.font-bold
    (str number)]
   [:div.text-sm.text-gray-700 content]])

(defn panel
  [state]
  (let [username (:username state)
        username-error (login-panel/get-username-validation-error username)
        is-username-valid (login-panel/valid-instagram-username? username)
        login-link (:login-link state)
        login-link-error (get-login-link-validation-error login-link)
        is-login-link-valid (valid-login-link? login-link)]
   [:div.bg-white.shadow-xl.rounded-2xl.px-8.pt-8.pb-8.max-w-xl.w-full.mx-auto.flex.flex-col.items-center
    (common/instagram-logo)
    [:h2.text-xl.font-bold.text-gray-900.mb-1 "Email Connect"]
    [:p.text-sm.text-gray-500.mb-6.text-center "Alternative method to connect your Instagram account"]

    [:div.w-full.space-y-4
    (step 1
      [:span "Logout from "
       [:a.text-blue-600.hover:underline {:href "https://www.instagram.com/" :target "_blank"} "Instagram.com"]
       ". Click " [:span.font-medium "More"] " in the lower left corner, then " [:span.font-medium "Log out"] "."])

    [:img.w-full.rounded-lg.border.border-gray-200
     {:alt "logout" :src "/connect/email-connect/img/logout.jpg"}]

    (step 2
      [:span "Open the "
       [:a.text-blue-600.hover:underline
        {:href "https://www.instagram.com/accounts/password/reset/" :target "_blank"}
        "Instagram password reset page"]
       " and enter the username you want to connect."])

    [:img.w-full.rounded-lg.border.border-gray-200
     {:alt "password reset" :src "/connect/email-connect/img/password-reset.jpg"}]

    (step 3
      [:span "You will receive an email from Instagram that looks like this:"])

    [:img.w-full.rounded-lg.border.border-gray-200
     {:alt "instagram email" :src "/connect/email-connect/img/instagram-email.jpg"}]

    (step 4
      [:span "Right-click the " [:span.font-medium "Log in as"] " button (on a phone: press and hold the button), choose "
       [:span.font-medium "Copy link address"] ", and paste it below:"])

    [:div.w-full
     [:label.block.text-sm.font-medium.text-gray-700.mb-1 {:for "username"} "Username"]
     ;; Wrapper div for password-manager compatibility, see comment in
     ;; `storrito.connect3.login-panel/panel`.
     [:div
      [:input#username.w-full.px-4.py-2.border.rounded-md.shadow-sm.focus:outline-none.focus:ring-2.focus:ring-blue-500.focus:border-blue-500.text-gray-800.bg-gray-50.transition-all.duration-200
       {:class (cond
                 username-error ["border-red-300" "focus:ring-red-500" "focus:border-red-500"]
                 is-username-valid ["border-green-300" "focus:ring-green-500" "focus:border-green-500"]
                 :else ["border-gray-300"])
        :required true
        :type "text"
        :minLength 3
        :name "username"
        :placeholder "Username / Instagram handle"
        :value username
        ;; `:input` instead of `:change`, see comment in
        ;; `storrito.connect3.login-panel/panel`.
        :on {:input [[:store/assoc-in [:connect3 :username] :event/target.value]]}}]]
     (when username-error
       [:div.text-red-600.text-xs.mt-1 username-error])]

    [:div.w-full
     [:label.block.text-sm.font-medium.text-gray-700.mb-1 {:for "login-link"} "Login Link"]
     [:input#login-link.w-full.px-4.py-2.border.rounded-md.shadow-sm.focus:outline-none.focus:ring-2.focus:ring-blue-500.focus:border-blue-500.text-gray-800.bg-gray-50.transition-all.duration-200
      {:class (cond
                login-link-error ["border-red-300" "focus:ring-red-500" "focus:border-red-500"]
                is-login-link-valid ["border-green-300" "focus:ring-green-500" "focus:border-green-500"]
                :else ["border-gray-300"])
       :required true
       :minLength 20
       :type "text"
       :name "login-link"
       :placeholder "Paste the login link here"
       :value login-link
       :on {:input [[:store/assoc-in [:connect3 :login-link] :event/target.value]]}}]
     (when login-link-error
       [:div.text-red-600.text-xs.mt-1 login-link-error])]

    (common/error-message state)

    [:div.flex.justify-end.w-full.mt-8
     (button/button-primary-lg
       {:class "w-full"
        :disabled (not (and is-username-valid is-login-link-valid))
        :on {:click [[:connect3/send! (select-keys state
                                                   [:username
                                                    :login-link
                                                    :country
                                                    :proxy
                                                    :reconnect])]]}}
       [:span [:i.fas.fa-link.mr-2] "Start Connect"])]

    [:div.text-center.mt-4
     [:a.text-xs.text-blue-600.dark:text-blue-500.hover:underline.cursor-pointer
      {:on {:click [[:store/assoc-in [:connect3 :email-connect?] false]]}}
      "Back to regular login"]]]]))
